import { cornerSolutions } from './generators/shapes';

export function calculateWorktopSections(...args) {
  const [plannedShape] = args;
  const shapeId = plannedShape.getIn(['value', 'id']);

  switch (shapeId) {
    case 'rectangular': return calculateRectangularWorktopSections(...args);
    case 'l-shape': return calculateLShapeWorktopSections(...args);
    case 'u-shape': return calculateUShapeWorktopSections(...args);
    case 'ii-shape': return calculateIIShapeWorktopSections(...args);
    case 'irregular': return calculateIrregularShapeWorktopSections(...args);
    case 'v-shape': return calculateVShapeWorktopSections(...args);
    case 'c-shape': return calculateCShapeWorktopSections(...args);
    case 'g-shape': return calculateGShapeWorktopSections(...args);
    default: throw new Error('Unsupported shapeId');
  }
}

export function calculateGShapeWorktopSections(plannedShape, maxDepth, maxLength, overhangDimensions){
  let [lengthBack, lengthLeft, lengthRight, lengthFront, depthBack,
    depthLeft, depthFront, depthRight] = Array.from(plannedShape.get('measurements').values());

  if (depthBack > maxDepth || depthLeft > maxDepth || depthRight > maxDepth || depthFront > maxDepth ) {
    throw new Error('depth not allowed');
  }

  if (lengthBack <= depthLeft + depthRight || lengthLeft <= depthBack + depthFront || lengthRight <= depthBack || lengthFront <= depthLeft ) {
    throw new Error('length not allowed');
  }
  if (depthLeft > depthBack) {
    lengthBack -= depthLeft;
  } else {
    lengthLeft -= depthBack;
  }
  if (depthRight > depthBack) {
    lengthBack -= depthRight;
  } else {
    lengthRight -= depthBack;
  }
  if(depthFront  > depthLeft){
    lengthLeft -= depthFront
  }else{
    lengthFront -= depthLeft
  }
 
  const back = calculateRectangularSections(depthBack, lengthBack, maxLength, !!overhangDimensions);
  const left = calculateRectangularSections(depthLeft, lengthLeft, maxLength, !!overhangDimensions);
  const front = calculateRectangularSections(depthFront, lengthFront, maxLength, !!overhangDimensions);
  const right = calculateRectangularSections(depthRight, lengthRight, maxLength, !!overhangDimensions);  
  return {
    sections: back.sections.concat(left.sections).concat(right.sections).concat(front.sections),
    joints: back.joints + left.joints + right.joints + front.joints + 3,
  };
}

export function calculateKitchenIslandSections(measurements, maxDepth, maxLength, doubleSided) {
  const length = measurements.get('a');
  const depth = measurements.get('b');
  if (depth > maxDepth) {
    throw new Error('depth not allowed');
  }
  return calculateRectangularSections(depth, length, maxLength, doubleSided);
}

function calculateRectangularSections(depth, l, maxLength, doubleSided = false) {
  let length = l;
  const sections = [];

  if (isNaN(maxLength)) {
    sections.push({ length, depth });
  } else {
    while (length > 0) {
      sections.push({ length: Math.min(length, maxLength), depth, doubleSided });
      length -= maxLength;
    }
  }

  return {
    sections,
    joints: sections.length - 1,
  };
}

function calculateRectangularSectionsIIshape(depth, l, maxLength, doubleSided = false, overhangSideId, currentIIShape) {
  let length = l;
  const sections = [];
  const nrOfWaterfalls = getNrOfWaterfallsPerShapeInIIWorktop(overhangSideId, currentIIShape);
  if (isNaN(maxLength)) {
    sections.push({ length, depth, nrOfWaterfalls });
  } else {
    while (length > 0) {
      sections.push({ length: Math.min(length, maxLength), depth, doubleSided, nrOfWaterfalls });
      length -= maxLength;
    }
  }

  return {
    sections,
    joints: sections.length - 1,
  };
}

export function calculateWaterfallSections(plannedShape, maxDepth, waterfallHeight, overhangDimensions) {
  const waterfallSections = overhangDimensions.map((dimension) => {
    const depthDimension = dimension.get('depth');
    const depth = plannedShape.getIn(['measurements', depthDimension]);
    if (depth > maxDepth) {
      throw new Error('depth not allowed');
    }
    return {
      length: waterfallHeight,
      depth,
      doubleSided: true,
    };
  }).toArray();
  return {
    sections: waterfallSections,
    joints: waterfallSections.length,
  };
}

function calculateRectangularWorktopSections(plannedShape, maxDepth, maxLength, overhangDimensions) {
  const length = plannedShape.getIn(['measurements', 'a']);
  const depth = plannedShape.getIn(['measurements', 'b']);
  if (depth > maxDepth) {
    throw new Error('depth not allowed');
  }
  return calculateRectangularSections(depth, length, maxLength, !!overhangDimensions);
}

function calculateLShapeWorktopSections(plannedShape, maxDepth, maxLength, overhangDimensions) {
  let lengthA = plannedShape.getIn(['measurements', 'a']);
  let lengthB = plannedShape.getIn(['measurements', 'b']);
  const depthC = plannedShape.getIn(['measurements', 'c']);
  const depthD = plannedShape.getIn(['measurements', 'd']);

  if (depthC > maxDepth || depthD > maxDepth) {
    throw new Error('depth not allowed');
  }

  if (lengthA <= depthD || lengthB <= depthC) {
    throw new Error('length not allowed');
  }

  if (depthC > depthD) {
    lengthB -= depthC;
  } else {
    lengthA -= depthD;
  }
  const a = calculateRectangularSections(depthC, lengthA, maxLength, !!overhangDimensions);
  const b = calculateRectangularSections(depthD, lengthB, maxLength, !!overhangDimensions);
  return {
    sections: a.sections.concat(b.sections),
    joints: a.joints + b.joints + 1,
  };
}

function calculateUShapeWorktopSections(plannedShape, maxDepth, maxLength, overhangDimensions) {
  let lengthBack = plannedShape.getIn(['measurements', 'a']);
  let lengthLeft = plannedShape.getIn(['measurements', 'b']);
  let lengthRight = plannedShape.getIn(['measurements', 'c']);
  const depthBack = plannedShape.getIn(['measurements', 'd']);
  const depthLeft = plannedShape.getIn(['measurements', 'e']);
  const depthRight = plannedShape.getIn(['measurements', 'f']);

  if (depthBack > maxDepth || depthLeft > maxDepth || depthRight > maxDepth) {
    throw new Error('depth not allowed');
  }

  if (lengthBack <= depthLeft + depthRight || lengthLeft <= depthBack || lengthRight <= depthBack) {
    throw new Error('length not allowed');
  }

  if (depthLeft > depthBack) {
    lengthBack -= depthLeft;
  } else {
    lengthLeft -= depthBack;
  }
  if (depthRight > depthBack) {
    lengthBack -= depthRight;
  } else {
    lengthRight -= depthBack;
  }  
  const back = calculateRectangularSections(depthBack, lengthBack, maxLength, !!overhangDimensions);
  const left = calculateRectangularSections(depthLeft, lengthLeft, maxLength, !!overhangDimensions);
  const right = calculateRectangularSections(depthRight, lengthRight, maxLength, !!overhangDimensions);
  return {
    sections: back.sections.concat(left.sections).concat(right.sections),
    joints: back.joints + left.joints + right.joints + 2,
  };
}

function calculateIIShapeWorktopSections(plannedShape, maxDepth, maxLength, overhangDimensions, doubleSidedSelected, overhangSideId) {
  const lengthA = plannedShape.getIn(['measurements', 'a']);
  const depthB = plannedShape.getIn(['measurements', 'b']);
  const lengthC = plannedShape.getIn(['measurements', 'c']);
  const depthD = plannedShape.getIn(['measurements', 'd']);

  if (depthB > maxDepth || depthD > maxDepth) {
    throw new Error('depth not allowed');
  }
  const isDoubleSidedWithoutOverhang = (overhangDimensions === true && (typeof overhangDimensions !== 'object'));
  const isDoubleSidedWithOverhang = (overhangDimensions !== true && (typeof overhangDimensions === 'object'));
  let doubleSided;
  if (isDoubleSidedWithOverhang) {
    doubleSided = ((overhangDimensions !== true && overhangDimensions) && overhangDimensions.reduce((total, dim) => ({
      ...total,
      [dim.get('length')]: true,
    }), {})) || {};
  } else if (isDoubleSidedWithoutOverhang) {
    doubleSided = isDoubleSidedWithoutOverhang;
  } else {
    doubleSided = !!overhangDimensions;
  }

  let a;
  let b;
  const firstIIshape = 'firstIIShape';
  const secondIIshape = 'secondIIShape';
  if (isDoubleSidedWithoutOverhang) {
    a = calculateRectangularSectionsIIshape(depthB, lengthA, maxLength, doubleSided);
    b = calculateRectangularSectionsIIshape(depthD, lengthC, maxLength, doubleSided);
  } else if (isDoubleSidedWithOverhang && !doubleSidedSelected) {
    a = calculateRectangularSectionsIIshape(depthB, lengthA, maxLength, doubleSided.a, overhangSideId, firstIIshape);
    b = calculateRectangularSectionsIIshape(depthD, lengthC, maxLength, doubleSided.c, overhangSideId, secondIIshape);
  } else if (isDoubleSidedWithOverhang && doubleSidedSelected) {
    // if double sided is selected by the user, both worktops should have double-sided polishing, regardless of which worktop has a waterfall
    a = calculateRectangularSectionsIIshape(depthB, lengthA, maxLength, doubleSidedSelected, overhangSideId, firstIIshape);
    b = calculateRectangularSectionsIIshape(depthD, lengthC, maxLength, doubleSidedSelected, overhangSideId, secondIIshape);
  } else {
    a = calculateRectangularSectionsIIshape(depthB, lengthA, maxLength, doubleSided);
    b = calculateRectangularSectionsIIshape(depthD, lengthC, maxLength, doubleSided);
  }

  return {
    sections: a.sections.concat(b.sections),
    joints: a.joints + b.joints,
  };
}

function calculateVShapeWorktopSections(plannedShape, maxDepth, maxLength, overhangDimensions) {
  const doubleSided = !!overhangDimensions;
  const depthC = plannedShape.getIn(['measurements', 'c']);
  const depthD = plannedShape.getIn(['measurements', 'd']);
  const widthOrHeightOfFrontEdge = 431;
  const cornerLength = widthOrHeightOfFrontEdge + depthD;
  const cornerDepth = widthOrHeightOfFrontEdge + depthC;

  const cornerRemovalFrontAmount = (((cornerDepth - depthC) * (cornerLength - depthD)) / 2) / 1000000;

  const deepCorner = {
    length: cornerLength,
    depth: cornerDepth,
    lengthFront: cornerSolutions.frontEdge,
    isVshapeCorner: true,
    cornerRemovalAmount: cornerRemovalFrontAmount,
    doubleSided,
    cornerAddDoubleSidedAmount: cornerDoubleSidedSidesArea(depthC, depthD, 'v-shape'),
  };
  const lengthA = plannedShape.getIn(['measurements', 'a']);
  const lengthB = plannedShape.getIn(['measurements', 'b']);
  const normalWorktopDepth = 635;
  const deepCornerDiff = cornerSolutions.deepCornerVshape;

  if (deepCorner.depth > maxDepth) {
    throw new Error('depth not allowed');
  }

  if (lengthA < deepCornerDiff || lengthB < deepCornerDiff) {
    throw new Error('length not allowed');
  }

  const a = calculateRectangularSections(normalWorktopDepth, lengthA - deepCornerDiff, maxLength, doubleSided);
  const b = calculateRectangularSections(normalWorktopDepth, lengthB - deepCornerDiff, maxLength, doubleSided);

  return {
    sections: a.sections.concat(b.sections).concat([deepCorner]),
    joints: a.joints + b.joints + 2,
    singleCuts: 2,
  };
}

function calculateCShapeWorktopSections(plannedShape, maxDepth, maxLength, overhangDimensions) {
  const doubleSided = !!overhangDimensions;

  const depthC = plannedShape.getIn(['measurements', 'c']);
  const depthD = plannedShape.getIn(['measurements', 'd']);
  const widthOrHeightOfFrontEdge = 431;
  const cornerLength = widthOrHeightOfFrontEdge + depthD;
  const cornerDepth = widthOrHeightOfFrontEdge + depthC;
  const deepCornerDiff = cornerSolutions.deepCornerCshape;

  const cornerRemovalFrontAmount = (((cornerLength - depthC) * (cornerDepth - depthD)) / 2) / 1000000;
  const cornerRemovalBackAmount = (((cornerLength - deepCornerDiff) * (cornerDepth - deepCornerDiff)) / 2) / 1000000;

  const deepCorner = {
    length: cornerLength,
    depth: cornerDepth,
    depthCentralized: (depthC + depthD) / 2,
    lengthFront: cornerSolutions.frontEdge,
    isCshapeCorner: true,
    cornerRemovalAmount: cornerRemovalFrontAmount + cornerRemovalBackAmount,
    doubleSided,
    cornerAddDoubleSidedAmount: cornerDoubleSidedSidesArea(depthC, depthD, 'c-shape'),
  };
  const lengthA = plannedShape.getIn(['measurements', 'a']);
  const lengthB = plannedShape.getIn(['measurements', 'b']);
  const normalWorktopDepth = 635;
  const distanceToJoint = deepCornerDiff + 150;
  if (deepCorner.depth > maxDepth) {
    throw new Error('depth not allowed');
  }

  if (lengthA < distanceToJoint || lengthB < distanceToJoint) {
    throw new Error('length not allowed');
  }
  const a = calculateRectangularSections(normalWorktopDepth, lengthA - deepCornerDiff, maxLength, doubleSided);
  const b = calculateRectangularSections(normalWorktopDepth, lengthB - deepCornerDiff, maxLength, doubleSided);

  return {
    sections: a.sections.concat(b.sections).concat([deepCorner]),
    joints: a.joints + b.joints + 2,
    singleCuts: 2,
  };
}

function calculateIrregularShapeWorktopSections(plannedShape, maxDepth, maxLength, overhangDimensions) {
  let length = plannedShape.getIn(['measurements', 'a']);
  const depthB = plannedShape.getIn(['measurements', 'b']);
  const depthC = plannedShape.getIn(['measurements', 'c']);

  if (depthB > maxDepth || depthC > maxDepth) {
    throw new Error('depth not allowed');
  }
  const highDepth = Math.max(depthB, depthC);
  const lowDepth = Math.min(depthB, depthC);
  const slope = (highDepth - lowDepth) / length;
  let depth = highDepth;
  const sections = [];
  while (length > 0) {
    sections.push({ length: Math.min(length, maxLength), depth, doubleSided: !!overhangDimensions });
    length -= maxLength;
    depth -= maxLength * slope;
  }
  return {
    sections,
    joints: sections.length - 1,
    singleCuts: depthB === depthC ? 0 : sections.length,
  };
}

function getNrOfWaterfallsPerShapeInIIWorktop(overhangSideId, currentIIShape) {
  if (currentIIShape === 'firstIIShape') {
    return getFirstShapeWaterfalls(overhangSideId);
  } else if (currentIIShape === 'secondIIShape') {
    return getSecondShapeWaterfalls(overhangSideId);
  } else {
    console.error("Shape sections calculation - could not find II-shape: ", currentIIShape);
    return 0;
  }
}

function getFirstShapeWaterfalls(overhangSideId) {
  if (overhangSideId === 'one-side-top-left' || overhangSideId === 'one-side-top-right' || overhangSideId === "two-sides-bottom-right-top-left" || 
  overhangSideId === "two-sides-bottom-left-top-right" || overhangSideId === "two-sides-left" || overhangSideId ==="two-sides-right" ||
  overhangSideId === "three-sides-not-top-right" ||overhangSideId === "three-sides-not-top-left") {
    return 1;
  } else if (overhangSideId === 'two-sides-top' || overhangSideId ==="three-sides-not-bottom-left" || overhangSideId === "three-sides-not-bottom-right" || overhangSideId === "four-sides") {
    return 2;
  }
  return 0;
}

function getSecondShapeWaterfalls(overhangSideId) {
  if (overhangSideId === 'one-side-bottom-left' || overhangSideId === 'one-side-bottom-right' || overhangSideId === "two-sides-bottom-right-top-left" || 
  overhangSideId === "two-sides-bottom-left-top-right" || overhangSideId === "two-sides-left" || overhangSideId === "two-sides-right" ||
  overhangSideId ==="three-sides-not-bottom-left" || overhangSideId === "three-sides-not-bottom-right") {
    return 1;
  } else if (overhangSideId === 'two-sides-bottom' || overhangSideId === "three-sides-not-top-right" || overhangSideId === "three-sides-not-top-left" || overhangSideId === "four-sides") {
    return 2;
  }
  return 0;
}

function cornerDoubleSidedSidesArea(depthC, depthD, shapeId) {
  let doubleSidedTriangleAreaTopSide;
  let doubleSidedTriangleAreaBotSide;
  if (depthD === null || depthD === undefined) {
    console.error("Depth C in ", shapeId, "is not defined");
  }
  if (depthD === null || depthD === undefined) {
    console.error("Depth D in ", shapeId, "is not defined");
  }
  if (depthD < 301) {
    doubleSidedTriangleAreaBotSide = (depthD * depthD * Math.sin(45)) / 1000000;
  } else {
    doubleSidedTriangleAreaBotSide = (300 * 300 * Math.sin(45)) / 1000000;
  }
  if (depthC < 301) {
    doubleSidedTriangleAreaTopSide = (depthC * depthC * Math.sin(45)) / 1000000;
  } else {
    doubleSidedTriangleAreaTopSide = (300 * 300 * Math.sin(45)) / 1000000;
  }
  return Number(((doubleSidedTriangleAreaTopSide + doubleSidedTriangleAreaBotSide) / 2).toFixed(4));
}
